import React, { useEffect } from 'react';
import useState from 'react-usestateref';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Pagination from '@material-ui/lab/Pagination';
import Checkbox from '@material-ui/core/Checkbox';
import { toast } from 'bulma-toast';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import ManageCommentCard from '../manage-comments-commentcard';
import IconText from '../../../molecules/icon-text/IconText';
import ModalContainer from '../../../molecules/modal-container/ModalContainer';
import CommentDropdown from '../../../atoms/comments/comment-dropdown/CommentDropdown.tsx';
import Utils from '../../../../utils/comments-utils';
import styles from './styles.scss';
import ErrorBoundary from '../../../atoms/error-boundary';

const ManageCommentForm = function ManageCommentForm(props) {
    const {
        baseurl, targetkey, charityslug,
    } = props;
    const { host, protocol } = new URL(baseurl);

    // pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // comments states
    const [commentNumber, setCommentNumber] = useState(0);
    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    // delete states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteCommentKey, setDeleteCommentKey] = useState();

    // order and filtering states
    const [searchText, setSearchText] = useState('');
    const [sortType, setSortType] = useState(Utils.SortOrders.Newest.Key);

    function getApiRequestUrl(sortByType, newCurrentPage) {
        const page = newCurrentPage ?? currentPage;
        const skip = (page - 1) * rowsPerPage;
        const orderByText = Utils.GetOrderByQuery(sortByType);

        // search text should be case insensitive
        const searchKeyword = `'${searchText.toLowerCase()}'`;
        let anonymousSearch = '';
        if ('anonymous'.includes(searchText.toLowerCase())) {
            anonymousSearch = 'or (author eq \'\')';
        }

        const baseUrl = `${protocol}//${host}`;
        const searchQuery = `$apply=filter(querykey eq '${targetkey}' and (parent eq '') and (contains(tolower(author), ${searchKeyword}) ${anonymousSearch}))`;
        const orderByQuery = `$orderby=${orderByText}&$top=${rowsPerPage}&$skip=${skip}&$count=true`;
        const url = `${baseUrl}/odata/messages?${searchQuery}&${orderByQuery}`;

        return url;
    }

    const buildPageUrl = (appealPath, groupPath) => {
        const baseUrl = new URL(window.location);
        if (baseUrl.host.startsWith('service.')) {
            // e.g. foodbank-victoria.giveeasy.org
            baseUrl.host = `${charityslug}.${baseUrl.host.slice(8)}`;
            baseUrl.pathname = `${appealPath}`;
        } else {
            baseUrl.pathname = `features/page/${charityslug}/${appealPath}`;
        }

        if (groupPath) {
            baseUrl.pathname = `${baseUrl.pathname}/${groupPath}`;
        }

        return baseUrl.toString();
    };

    const generateRows = (commentsList) => {
        const commentRows = commentsList.map((c) => {
            const singleComment = {};
            singleComment.key = c.Key;
            singleComment.amount = c.Amount;

            const singleManageCommentCard = (
                <ManageCommentCard
                    key={c.Key}
                    author={c.Author}
                    messagetext={c.MessageText}
                    created={c.Created}
                    amount={c.Amount}
                    targetname={c.TargetName}
                    pageUrl={buildPageUrl(c.AppealPath, c.GroupPath)}
                    reply={c.Reply}
                    messageKey={c.Key}
                    targetKey={c.TargetKey}
                    baseurl={baseurl}
                />
            );
            singleComment.donations = singleManageCommentCard;
            return singleComment;
        });
        return commentRows;
    };

    // Utils.GetNestedComments handles associating replies to parent messages
    // but we need to pass the replies in the result set for it to handle.
    function setNestedComments(parentComments, replyComments) {
        const combined = parentComments.concat(replyComments);
        const nestedComments = Utils.GetNestedComments(null, combined);

        const newRows = generateRows(nestedComments);
        setRows(newRows);
    }

    // fetch all replies
    async function fetchReplies(parents) {
        if (parents && parents.length > 0) {
            const parentKeys = parents.map((c) => c.Key).join('\',\'');
            const query = `${protocol}//${host}/odata/messages?$apply=filter(parent in ('${parentKeys}'))&$top=${rowsPerPage}`;

            await fetch(query, { method: 'GET' })
                .then((response) => {
                    if (response.status === 200) {
                        return response.json();
                    }

                    return [];
                })
                .then((response) => {
                    setNestedComments(parents, response.value ?? []);
                })
                .catch(() => {
                    setNestedComments(parents, []);
                });
        } else {
            // we need to deal with no data in api response (specifically for search).
            // otherwise there is no state update.
            setRows([]);
        }

        return [];
    }

    function handleComments(response) {
        const count = response['@odata.count'];
        setCommentNumber(count);

        const parents = response.value;

        fetchReplies(parents);
    }

    async function fetchComments(query) {
        let status = 0;

        await fetch(query, { method: 'GET' })
            .then((response) => {
                status = response.status;
                return response.json();
            })
            .then((response) => {
                if (status === 200) {
                    handleComments(response, status);
                }
            });
    }

    const useStyles = makeStyles({
        root: {
            width: '100%',
        },
        container: {
            maxHeight: 1000,
        },
        searchBar: {
            '& .MuiOutlinedInput-root': {
                '& fieldset': {
                    borderColor: '#676767',
                },
                '&:hover fieldset': {
                    borderColor: '#676767',
                },
                '&.Mui-focused fieldset': {
                    border: '1px solid #676767',
                },
            },
        },
    });
    const classes = useStyles();

    /**
    *  handle select checkbox for phase2
    */

    // handle select all comments
    // const handleSelectAllClick = (event) => {
    //     if (event.target.checked) {
    //         const newSelecteds = rows.map((n) => n.key);
    //         setSelectedRows(newSelecteds);
    //         return;
    //     }
    //     setSelectedRows([]);
    // };

    const isSelected = (key) => selectedRows.indexOf(key) !== -1;

    // handle select comment
    const handleSelect = (key) => {
        const selectedIndex = selectedRows.indexOf(key);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedRows, key);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedRows.slice(1));
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelected = newSelected.concat(selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1),
            );
        }
        setSelectedRows(newSelected);
    };

    const columns = [
        /**
         * checkbox for phase2
         */
        // {
        //     id: 'checkbox',
        //     label:
        //         (
        //             <Checkbox
        //                 color="primary"
        //                 onChange={handleSelectAllClick}
        //                 inputProps={{
        //                     'aria-label': 'select all desserts',
        //                 }}
        //             />
        //         ),
        //     padding: 'checkbox',
        // },
        {
            id: 'donations', label: 'COMMENTS', minWidth: 170, width: '60%',
        },
        {
            id: 'amount',
            label: 'AMOUNT',
            minWidth: 100,
            format: ((value) => value.toLocaleString('en-US')),
        },
        {
            id: 'action', label: 'ACTION', minWidth: 100,
        },
    ];

    const toastConfig = {
        position: 'bottom-center',
        duration: 2000,
        closeOnClick: true,
        pauseOnHover: true,
    };

    const deleteComments = () => {
        fetch(`${protocol}//${host}/odata/messages/${deleteCommentKey}`, { method: 'DELETE' })
            .then((response) => {
                if (response.status !== 204) {
                    throw new Error('Delete Comment Failed!');
                } else {
                    toast({
                        ...toastConfig,
                        message: 'Comment deleted successfully!',
                        type: 'is-primary',
                    });
                }
            })
            .then(() => {
                const newRows = rows.filter((c) => c.key !== deleteCommentKey);
                setRows(newRows);
                setDeleteCommentKey();

                const newcount = commentNumber - 1;
                setCommentNumber(newcount);
            });
    };

    function handleRowsPerPage(newRowPerPage) {
        const currentTotalPages = Math.ceil(commentNumber / newRowPerPage);

        if (totalPages !== currentTotalPages) {
            setTotalPages(currentTotalPages);
            // when total pages changed and currentPage is larger than currentTotalPages,
            // set currentPage to the first page
            if (currentPage > currentTotalPages) {
                setCurrentPage(1);
            }
        }

        setRowsPerPage(newRowPerPage);
    }

    useEffect(() => {
        fetchComments(getApiRequestUrl(sortType));
    }, []);

    useEffect(() => {
        setTotalPages(Math.ceil(commentNumber / rowsPerPage));
    }, [rows]);

    const rowsPerPageHandler = (value) => {
        const newRowPerPage = parseInt(value, 10);
        handleRowsPerPage(newRowPerPage);
    };

    const deleteModal = (
        <ModalContainer
            showmodal={showDeleteModal}
            showheader
            showfooter
            modaltitle="Are you sure you want to delete this comment?"
            modalclosebuttontext="Cancel"
            confirmbuttontext="Delete"
            confirmaction={deleteComments}
            toggleshow={setShowDeleteModal}
        >
            <p>
                This comment and any replies will be deleted immediately.
                You cannot undo this action.
            </p>
        </ModalContainer>
    );

    const pageHandler = async (event, value) => {
        setCurrentPage(value);
    };

    function search() {
        if (currentPage === 1) {
            fetchComments(getApiRequestUrl(sortType));
        } else {
            // this will fire fetchComments through useEffect.
            // not great, but will reduce the number of api calls made
            setCurrentPage(1);
        }
    }

    useEffect(() => {
        if (searchText) {
            // delay 1s to wait user end typing, then do the ApiRequest
            const delayApiRequest = setTimeout(() => {
                search();
            }, 1000);
            return () => clearTimeout(delayApiRequest);
        }

        search();

        return null;
    }, [searchText]);

    useEffect(() => {
        fetchComments(getApiRequestUrl(sortType));
    }, [currentPage]);

    useEffect(() => {
        // when rowsPerPage or currentPage changed, re-fetch comments
        fetchComments(getApiRequestUrl(sortType, 1));
    }, [rowsPerPage]);

    useEffect(() => {
        // we need to try and limit the amount of setStates we call at a time
        // if there are too many, some of these calls may be swallowed
        // chaining useEffect hooks will ensure setStates are all being queued.
        if (currentPage === 1) {
            fetchComments(getApiRequestUrl(sortType));
        } else {
            setCurrentPage(1);
        }
    }, [sortType]);

    const searchHandler = (event) => {
        const newSearchText = event.target.value;
        setSearchText(newSearchText);
    };

    useEffect(() => {
        // reset deleteCommentKey as undefined if close delete modal
        if (!showDeleteModal && deleteCommentKey) {
            setDeleteCommentKey();
        }
    }, [showDeleteModal]);

    const displayedNumber = `COMMENTS (${commentNumber})`;

    return (
        <ErrorBoundary>
            <>
                <div>
                    {displayedNumber}
                </div>
                <div className={styles['fullwidth-div']}>
                    <div className={styles['left-div']}>
                        <TextField
                            variant="outlined"
                            value={searchText}
                            onChange={(event) => searchHandler(event)}
                            placeholder="Find a donor"
                            size="small"
                            className={`${classes.searchBar} ${styles['search-bar']}`}
                            InputProps={{
                                startAdornment: <SearchIcon fontSize="small" />,
                                endAdornment: (
                                    <IconButton
                                        title="Clear"
                                        aria-label="Clear"
                                        size="small"
                                        style={{ visibility: searchText === '' ? 'hidden' : 'visible' }}
                                        onClick={() => { setSearchText(''); }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                        />
                    </div>

                    <div className={styles['right-div']}>
                        <div className={styles.footer}>
                            <div className={styles.label}>Sort by</div>
                            <CommentDropdown
                                id="SortBy"
                                testid="Sortby-dropdown"
                                hasPlaceholder
                                options={new Map(
                                    [
                                        [Utils.SortOrders.Newest.Key, Utils.SortOrders.Newest.Value],
                                        [Utils.SortOrders.Oldest.Key, Utils.SortOrders.Oldest.Value],
                                        [Utils.SortOrders.HighestAmount.Key, Utils.SortOrders.HighestAmount.Value],
                                        [Utils.SortOrders.LowestAmount.Key, Utils.SortOrders.LowestAmount.Value],
                                    ],
                                )}
                                selectHandler={(value) => setSortType(value)}
                                selectedValue={sortType}
                            />
                        </div>
                    </div>
                </div>
                <div style={{ clear: 'both' }}>
                    {deleteModal}
                    <Paper className={classes.root}>
                        <TableContainer className={classes.container}>
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell
                                                className={styles['table-header']}
                                                padding={column.padding}
                                                key={column.id}
                                                style={{
                                                    minWidth: column.minWidth,
                                                    backgroundColor: '#95B5C7',
                                                    textAlign: column.id === 'donations' ? 'left' : 'center',
                                                    color: '#FFFFFF',
                                                    paddingLeft: column.id === 'donations' ? '31px' : null,
                                                }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody data-testid="comments-table-body">
                                    {rows.map((row) => (
                                        <TableRow data-testid={`${row.Key}-row`} hover role="checkbox" tabIndex={-1} key={row.key} aria-label="table-row">
                                            {columns.map((column) => {
                                                let value = null;
                                                if (column.id === 'action') {
                                                    value = (
                                                        <IconText
                                                            testid={`${row.key}-delete`}
                                                            type="button"
                                                            text="Delete"
                                                            iconPath="M9 4.75V12.25H3V4.75H9ZM7.875 0.25H4.125L3.375 1H0.75V2.5H11.25V1H8.625L7.875 0.25ZM10.5 3.25H1.5V12.25C1.5 13.075 2.175 13.75 3 13.75H9C9.825 13.75 10.5 13.075 10.5 12.25V3.25Z"
                                                            svgWidth="12"
                                                            svgHeight="14"
                                                            svgViewBox="0 0 12 14"
                                                            iconTextStyle={styles.remove}
                                                            clickHandler={
                                                                () => {
                                                                    setShowDeleteModal(true);
                                                                    setDeleteCommentKey(
                                                                        row.key,
                                                                    );
                                                                }
                                                            }
                                                        />
                                                    );
                                                } else if (column.id === 'checkbox') {
                                                    value = (
                                                        <Checkbox
                                                            color="primary"
                                                            checked={isSelected(row.key)}
                                                            onClick={
                                                                () => handleSelect(row.key)
                                                            }
                                                        />
                                                    );
                                                } else if (column.id === 'amount') {
                                                    value = `$${row[column.id]}`;
                                                } else {
                                                    value = row[column.id];
                                                }
                                                return (
                                                    <TableCell
                                                        key={column.id}
                                                        style={{
                                                            width: column.width,
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {column.format && typeof value === 'number' ? column.format(value) : value}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                    <div className={styles.footer}>
                        <div className={styles.label}>Rows per page</div>
                        <CommentDropdown
                            testid="page-dropdown"
                            labelName="Rows per page"
                            hasPlaceholder
                            options={new Map(
                                [
                                    [10, '10'],
                                    [20, '20'],
                                    [50, '50'],
                                    [100, '100'],
                                ],
                            )}
                            selectHandler={rowsPerPageHandler}
                            selectedValue={rowsPerPage}
                        />
                        <Pagination count={totalPages} color="primary" className={styles.pagination} page={currentPage} onChange={pageHandler} />
                    </div>
                </div>

            </>
        </ErrorBoundary>
    );
};

ManageCommentForm.propTypes = {
    baseurl: PropTypes.string,
    targetkey: PropTypes.string.isRequired,
    charityslug: PropTypes.string.isRequired,
};

ManageCommentForm.defaultProps = {
    baseurl: window.location.href,
};

export default ManageCommentForm;
